import { useEffect, useState } from "react";
import {
	Button,
	Field,
	Label,
	Menu,
	MenuButton,
	MenuItem,
	MenuItems,
	Switch,
} from "@headlessui/react";
import {
	ArrowPathRoundedSquareIcon,
	EllipsisVerticalIcon,
	PlusIcon,
	PrinterIcon,
	TrashIcon,
} from "@heroicons/react/24/solid";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { isHigherThanDistrictAdmin, roleDescriptions, UserRole } from "../../app/userSlice";
import { useAppSelector } from "../../app/hooks";
import SimplePagination from "../../components/SimplePagination";
import { SimpleOption } from "../../components/input/SimpleListbox";
import { Loading } from "../../components/Loading";
import SearchFilter from "../../components/input/SearchFilter";
import Select from "../../components/input/Select";
import {
	useGetDistrictsQuery,
	useGetSchoolsByDistrictIdMutation,
} from "../districts/districtsApiSlice";
import UsersListTable from "./UsersListTable";
import {
	useGetUsersListQuery,
	useManageUserListQuery,
	UsersListQueryModel,
	roleColors,
} from "./usersApiSlice";

export default function UsersList() {
	const { role } = useAppSelector((state) => state.user);

	const _displayLength = 20;
	const initQueryargs: UsersListQueryModel = {
		iDisplayStart: 0,
		iDisplayLength: _displayLength,
		iSortCol_0: 0,
		sSortDir_0: "asc",
		roleFilter: "",
		nameFilter: "",
		showInactiveFilter: true,
		districtFilter: 0,
		schoolFilter: 0,
	};

	const [queryargs, setQueryargs] = useState(initQueryargs);
	const [page, setPage] = useState(0);
	const [searchFilter, setSearchFilter] = useState("");
	const [districtFilter, setDistrictFilter] = useState(0);
	const [schoolFilter, setSchoolFilter] = useState(0);
	const [roleFilter, setRoleFilter] = useState("");
	const [includeInactive, setIncludeInactive] = useState(false);
	const [isFiltered, setIsFiltered] = useState(false);

	const [districtOptions, setDistrictOptions] = useState<SimpleOption[]>([]);
	const [schoolOptions, setSchoolOptions] = useState<SimpleOption[]>([]);
	const [roleOptions, setRoleOptions] = useState<SimpleOption[]>([]);

	const manageUserListResult = useManageUserListQuery();
	const manage = manageUserListResult.data;

	const userListResult = useGetUsersListQuery(queryargs);
	const users = userListResult.data;

	const getDistrictsResult = useGetDistrictsQuery();
	const districts = getDistrictsResult.data;

	const [callGetSchoolsByDistrict] = useGetSchoolsByDistrictIdMutation();

	useEffect(() => {
		setQueryargs({
			...queryargs,
			iDisplayStart: _displayLength * page,
			roleFilter: roleFilter,
			nameFilter: searchFilter,
			showInactiveFilter: includeInactive,
			districtFilter: districtFilter,
			schoolFilter: schoolFilter,
		});
	}, [page, searchFilter, districtFilter, schoolFilter, roleFilter, includeInactive]);

	useEffect(() => {
		setIsFiltered(
			!!searchFilter ||
				!!schoolFilter ||
				!!roleFilter ||
				!!districtFilter ||
				!!includeInactive
		);
		setPage(0);
	}, [searchFilter, schoolFilter, roleFilter, districtFilter, includeInactive]);

	const handlePageChange = (page: number) => {
		setPage(page);
	};

	useEffect(() => {
		if (districts) {
			const options = districts.map((d) => ({ value: d.id, label: d.name }) as SimpleOption);
			options.unshift({ value: 0, label: "Any District" } as SimpleOption);

			setDistrictOptions(options);
		}
	}, [districts]);

	useEffect(() => {
		callGetSchoolsByDistrict({
			districtId: districtFilter === 0 ? undefined : districtFilter,
			filterInactive: true,
		})
			.unwrap()
			.then((schools) => {
				if (schools) {
					const options = schools.map(
						(s) => ({ value: s.id, label: s.name }) as SimpleOption
					);
					options.unshift({ value: 0, label: "Any School" } as SimpleOption);

					setSchoolOptions(options);
					setSchoolFilter(0);
				}
			});
	}, [districtFilter]);

	useEffect(() => {
		if (!manage) return;
		const roles = manage.viewableRoles.map((r) => {
			return {
				value: r,
				label: roleDescriptions[r],
				color: roleColors[r],
			} as SimpleOption;
		});
		roles.unshift({ value: "", label: "Any Role" } as SimpleOption);
		setRoleOptions(roles);
	}, [manage]);

	return (
		<section>
			<h1>Users</h1>
			{role === UserRole.SchoolAdmin ? (
				<div className="mt-4 flex flex-col xl:flex-row gap-4 xl:gap-28 items-end">
					<div className="flex flex-col md:flex-row w-full gap-4 items-end">
						<SearchFilter
							id="searchUsers"
							name="searchUsers"
							searchFilter={searchFilter}
							setSearchFilter={setSearchFilter}
							label="Search"
							placeHolder="Search by name, username or by full external ID..."
							className="w-full md:w-auto flex-1"
							debounceDelay
						/>
						<Select
							value={roleFilter}
							setValue={(v) => setRoleFilter(v as string)}
							label="Role"
							simpleListBoxId="roleFilter"
							options={roleOptions}
							fieldClasses="w-full flex-none md:w-48 pt-2 md:pt-0"
							simpleListBoxClasses="w-full"
						/>
						<Field className="w-full md:w-auto flex-none">
							<Label
								as="span"
								className="block mb-2 text-sm font-medium text-gray-90 pt-2 md:pt-0">
								Include Inactive Users
							</Label>
							<Switch
								checked={includeInactive}
								onChange={setIncludeInactive}
								className="group relative inline-flex h-8 w-14 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 data-[checked]:bg-primary-600">
								<span
									aria-hidden="true"
									className="pointer-events-none inline-block size-7 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-6"
								/>
							</Switch>
						</Field>
					</div>
					<UserListButtons />
				</div>
			) : (
				<>
					<div className="flex flex-col md:flex-row w-full mt-4 gap-4 items-end">
						<Select
							value={districtFilter}
							setValue={(v) => setDistrictFilter(v as number)}
							label="District"
							simpleListBoxId="districtFilter"
							options={districtOptions}
							fieldClasses="w-full md:flex-1"
							simpleListBoxClasses="w-full"
						/>
						<Select
							value={schoolFilter}
							setValue={(v) => setSchoolFilter(v as number)}
							label="School"
							simpleListBoxId="schoolFilter"
							options={schoolOptions}
							fieldClasses="w-full md:flex-1 pt-2 md:pt-0"
							simpleListBoxClasses="w-full"
						/>
						<Select
							value={roleFilter}
							setValue={(v) => setRoleFilter(v as string)}
							label="Role"
							simpleListBoxId="roleFilter"
							options={roleOptions}
							fieldClasses="w-full md:flex-1 pt-2 md:pt-0"
							simpleListBoxClasses="w-full"
						/>
						<Field className="w-full md:w-auto">
							<Label
								as="span"
								className="block mb-2 text-sm font-medium text-gray-90 pt-2 md:pt-0">
								Include Inactive Users
							</Label>
							<Switch
								checked={includeInactive}
								onChange={setIncludeInactive}
								className="group relative inline-flex h-8 w-14 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 data-[checked]:bg-primary-600">
								<span
									aria-hidden="true"
									className="pointer-events-none inline-block size-7 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-6"
								/>
							</Switch>
						</Field>
					</div>
					<div className="flex flex-col md:flex-row w-full mt-4 gap-4 items-end">
						<SearchFilter
							id="searchUsers"
							name="searchUsers"
							searchFilter={searchFilter}
							setSearchFilter={setSearchFilter}
							label="Search"
							placeHolder="Search by name, username or by full external ID..."
							className="w-full md:w-auto flex-1"
							debounceDelay
						/>
						<UserListButtons />
					</div>
				</>
			)}
			<Loading loading={userListResult.isLoading} />
			{users && <UsersListTable users={users.aaData} handlePageChange={handlePageChange} />}
			{((users?.iTotalDisplayRecords ?? 0) > queryargs.iDisplayLength || isFiltered) && (
				<SimplePagination
					itemCount={users?.iTotalDisplayRecords}
					page={page}
					pageSize={queryargs.iDisplayLength}
					totalCount={users?.iTotalRecords}
					isFiltered={isFiltered}
					onChange={(page) => handlePageChange(page)}
					className={"border-none"}
				/>
			)}
		</section>
	);
}

function UserListButtons() {
	const { role } = useAppSelector((state) => state.user);
	return (
		<div className="flex gap-2 items-center">
			<Button id="add-user-button" className="btn btn-primary" onClick={() => {}}>
				<span className="flex items-center">
					<PlusIcon className="size-4 mr-2" /> Add
				</span>
			</Button>
			{role && isHigherThanDistrictAdmin(role) && (
				<>
					<Button id="merge-users-button" className="btn btn-neutral" onClick={() => {}}>
						<span className="flex items-center">
							<ArrowPathRoundedSquareIcon className="size-4 mr-2" /> Merge
						</span>
					</Button>
					<Button id="delete-users-button" className="btn btn-neutral" onClick={() => {}}>
						<span className="flex items-center">
							<TrashIcon className="size-4 mr-2" /> Delete
						</span>
					</Button>
				</>
			)}
			<PrintDownloadMenu />
		</div>
	);
}

function PrintDownloadMenu() {
	return (
		<Menu as="div" className="relative inline-block">
			<MenuButton className="flex items-center rounded-full text-gray-400 hover:text-gray-600">
				<span className="sr-only">Open options</span>
				<EllipsisVerticalIcon aria-hidden="true" className="size-8" />
			</MenuButton>

			<MenuItems
				anchor="bottom end"
				className="absolute z-10 pb-1 rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none hover:cursor-pointer">
				<MenuItem key="print-user-list">
					<a
						onClick={() => {}}
						className="block data-[focus]:bg-gray-100 px-4 py-2 text-sm text-gray-700">
						<div className="flex items-center">
							<PrinterIcon className="size-4 mr-2" aria-hidden="true" />
							Print User List
						</div>
					</a>
				</MenuItem>
				<MenuItem key="download-excel">
					<a
						onClick={() => {}}
						className="block data-[focus]:bg-gray-100 px-4 py-2 text-sm text-gray-700">
						<div className="flex items-center">
							<ArrowDownTrayIcon className="size-4 mr-2" aria-hidden="true" />
							Download Excel
						</div>
					</a>
				</MenuItem>
				<MenuItem key="download-csv">
					<a
						onClick={() => {}}
						className="block data-[focus]:bg-gray-100 px-4 py-2 text-sm text-gray-700">
						<div className="flex items-center">
							<ArrowDownTrayIcon className="size-4 mr-2" aria-hidden="true" />
							Download CSV
						</div>
					</a>
				</MenuItem>
			</MenuItems>
		</Menu>
	);
}
